// Pure helpers for the packet detail panel's Bytes section (components/PacketBytesSection.tsx); no
// imports so tests/packetBytes.test.mjs can run it directly. The server (GET /api/packets/{hash}/bytes)
// cuts the packet into fields; this turns those fields into rows of coloured bytes and decides what a
// hover highlights.

export const BYTES_PER_ROW = 16;

export type ByteGroup = "header" | "route" | "address" | "check" | "content" | "other";

export interface ByteField {
  start: number; // first byte
  end: number; // one past the last byte
  name: string;
  group: ByteGroup;
  value: string;
}

export interface PacketBytesResponse {
  packet_hash: string;
  available: boolean;
  source: string | null;
  observer: string | null; // the CoreScope station whose capture this is; null for our own radio
  path_hops: number | null; // hops carried in these bytes; null when unknown or a trace
  length: number;
  hex: string;
  fields: ByteField[];
  note: string | null;
}

// Plain labels for the colour key; the class name carries the tint and the line style (CSS 09-payload-mqtt-ask.css).
export const GROUP_INFO: Record<ByteGroup, { label: string; hint: string }> = {
  header: { label: "Header", hint: "Route and packet type" },
  route: { label: "Route", hint: "Transport codes, path length and path hops" },
  address: { label: "Address", hint: "Public keys and short hashes saying who it is for or from" },
  check: { label: "Check / signature", hint: "Signatures, MACs and checksums" },
  content: { label: "Content", hint: "Timestamps, flags, position, name and encrypted data" },
  other: { label: "Unexplained", hint: "Bytes the layout does not explain" },
};

export const GROUP_ORDER: ByteGroup[] = ["header", "route", "address", "check", "content", "other"];

/** "127 bytes, as heard by <station> (CoreScope)" or "... by our radio": which copy of the packet these bytes are. */
export function captionText(length: number, source: string | null, observer: string | null): string {
  const size = `${length} ${length === 1 ? "byte" : "bytes"}`;
  if (source === "CoreScope") return observer ? `${size}, as heard by ${observer} (CoreScope)` : `${size}, as heard by CoreScope`;
  return `${size}, as heard by our radio`;
}

/**
 * A line to show when the copy of the packet in these bytes carried a different number of hops than the
 * route shown above it (another observer's copy). Null when they agree or the bytes' hop count is unknown.
 */
export function hopNote(bytesHops: number | null, routeHops: number): string | null {
  if (bytesHops == null || bytesHops === routeHops) return null;
  return bytesHops < routeHops
    ? "Other observers heard this packet after more hops."
    : "Other observers heard this packet after fewer hops.";
}

export interface ByteCell {
  index: number; // position in the packet
  hex: string; // two lowercase hex digits
  field: number; // index into fields, -1 if none covers it
}

export interface ByteRow {
  offset: string; // four hex digits
  cells: ByteCell[];
}

/** Four hex digits, e.g. 16 -> "0010". */
export function formatOffset(n: number): string {
  return n.toString(16).padStart(4, "0");
}

/** Index of the field holding byte `i`, or -1. Fields are sorted and do not overlap. */
export function fieldAt(fields: ByteField[], i: number): number {
  let lo = 0;
  let hi = fields.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const f = fields[mid];
    if (i < f.start) hi = mid - 1;
    else if (i >= f.end) lo = mid + 1;
    else return mid;
  }
  return -1;
}

/** The packet's hex string laid out as rows of `perRow` bytes, each byte tagged with its field. */
export function byteRows(hex: string, fields: ByteField[], perRow: number = BYTES_PER_ROW): ByteRow[] {
  const total = Math.floor(hex.length / 2);
  const rows: ByteRow[] = [];
  let f = 0; // fields are in order, so walk forward instead of searching each byte
  for (let start = 0; start < total; start += perRow) {
    const cells: ByteCell[] = [];
    for (let i = start; i < Math.min(start + perRow, total); i++) {
      while (f < fields.length && i >= fields[f].end) f++;
      const inField = f < fields.length && i >= fields[f].start;
      cells.push({ index: i, hex: hex.slice(i * 2, i * 2 + 2).toLowerCase(), field: inField ? f : -1 });
    }
    rows.push({ offset: formatOffset(start), cells });
  }
  return rows;
}

/** "byte 3" or "bytes 4-35" (counting from 0, like the offsets). */
export function rangeText(f: ByteField): string {
  const last = f.end - 1;
  return last <= f.start ? `byte ${f.start}` : `bytes ${f.start}-${last}`;
}

/** The line shown for a hovered field: name, value and where it sits. */
export function fieldSummary(f: ByteField): string {
  return f.value ? `${f.name}: ${f.value} (${rangeText(f)})` : `${f.name} (${rangeText(f)})`;
}

/**
 * Which field is highlighted: the one under the pointer or the legend row being pointed at (`hover`),
 * otherwise the one a tap or key press pinned (`pinned`). Null when neither.
 */
export function activeField(hover: number | null, pinned: number | null): number | null {
  return hover ?? pinned;
}

/** A tap on a field pins it; a second tap on the same field lets go. */
export function togglePinned(pinned: number | null, tapped: number): number | null {
  return pinned === tapped ? null : tapped;
}

/** The groups that actually occur in this packet, in key order, so the colour key only lists what is on screen. */
export function groupsPresent(fields: ByteField[]): ByteGroup[] {
  const seen = new Set(fields.map((f) => f.group));
  return GROUP_ORDER.filter((g) => seen.has(g));
}
